<template>
  <Card class="max-w-2xl">
    <template #header>
      <h2 class="text-lg font-semibold text-gray-900 dark:text-white">User Preferences</h2>
      <p class="text-sm text-gray-600 dark:text-gray-300 mt-1">
        Customize your account settings and preferences
      </p>
    </template>

    <div class="space-y-8">
      <!-- Theme Settings -->
      <div>
        <h3 class="text-base font-medium text-gray-900 dark:text-white mb-4">Appearance</h3>
        <div class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
              Theme
            </label>
            <div class="grid grid-cols-3 gap-3">
              <button
                v-for="theme in themeOptions"
                :key="theme.value"
                type="button"
                @click="updateTheme(theme.value)"
                :class="[
                  'relative rounded-lg border-2 p-4 flex flex-col items-center space-y-2 focus:outline-none focus:ring-2 focus:ring-blue-500',
                  preferences.theme === theme.value
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                    : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500',
                ]"
              >
                <component :is="theme.icon" class="w-6 h-6 text-gray-600 dark:text-gray-300" />
                <span class="text-sm font-medium text-gray-900 dark:text-white">{{
                  theme.label
                }}</span>
                <CheckIcon
                  v-if="preferences.theme === theme.value"
                  class="absolute top-2 right-2 w-4 h-4 text-blue-500"
                />
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Notification Settings -->
      <div>
        <h3 class="text-base font-medium text-gray-900 dark:text-white mb-4">Notifications</h3>
        <div class="space-y-4">
          <div class="flex items-center justify-between">
            <div>
              <label class="text-sm font-medium text-gray-700 dark:text-gray-200"
                >Email Notifications</label
              >
              <p class="text-sm text-gray-500 dark:text-gray-400">
                Receive notifications via email
              </p>
            </div>
            <Switch
              :model-value="preferences.notifications.email"
              @update:model-value="toggleNotification('email')"
            />
          </div>

          <div class="flex items-center justify-between">
            <div>
              <label class="text-sm font-medium text-gray-700 dark:text-gray-200"
                >Browser Notifications</label
              >
              <p class="text-sm text-gray-500 dark:text-gray-400">
                Show notifications in your browser
              </p>
            </div>
            <Switch
              :model-value="preferences.notifications.browser"
              @update:model-value="toggleNotification('browser')"
            />
          </div>

          <div class="flex items-center justify-between">
            <div>
              <label class="text-sm font-medium text-gray-700 dark:text-gray-200"
                >Marketing Emails</label
              >
              <p class="text-sm text-gray-500 dark:text-gray-400">
                Receive product updates and marketing content
              </p>
            </div>
            <Switch
              :model-value="preferences.notifications.marketing"
              @update:model-value="toggleNotification('marketing')"
            />
          </div>
        </div>
      </div>

      <!-- Language and Region -->
      <div>
        <h3 class="text-base font-medium text-gray-900 dark:text-white mb-4">Language & Region</h3>
        <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label
              for="language"
              class="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2"
            >
              Language
            </label>
            <select
              id="language"
              v-model="preferences.language"
              @change="updateLanguage"
              class="block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            >
              <option value="en">English</option>
              <option value="es">Español</option>
              <option value="fr">Français</option>
              <option value="de">Deutsch</option>
              <option value="it">Italiano</option>
              <option value="ja">日本語</option>
              <option value="ko">한국어</option>
              <option value="zh">中文</option>
            </select>
          </div>

          <div>
            <label
              for="timezone"
              class="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2"
            >
              Timezone
            </label>
            <select
              id="timezone"
              v-model="preferences.timezone"
              @change="updateTimezone"
              class="block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            >
              <option v-for="tz in timezoneOptions" :key="tz.value" :value="tz.value">
                {{ tz.label }}
              </option>
            </select>
          </div>
        </div>
      </div>

      <!-- Data Export -->
      <div class="border-t border-gray-200 dark:border-gray-600 pt-8">
        <h3 class="text-base font-medium text-gray-900 dark:text-white mb-4">Data Management</h3>
        <div class="space-y-4">
          <div class="flex items-center justify-between">
            <div>
              <h4 class="text-sm font-medium text-gray-700 dark:text-gray-200">Export Data</h4>
              <p class="text-sm text-gray-500 dark:text-gray-400">
                Download a copy of your account data
              </p>
            </div>
            <Button variant="outline" size="sm" @click="exportData"> Export Data </Button>
          </div>
        </div>
      </div>

      <!-- Privacy Settings -->
      <div class="border-t border-gray-200 dark:border-gray-600 pt-8">
        <h3 class="text-base font-medium text-gray-900 dark:text-white mb-4">Privacy & Security</h3>
        <div class="space-y-4">
          <div class="flex items-center justify-between">
            <div>
              <label class="text-sm font-medium text-gray-700 dark:text-gray-200"
                >Activity Tracking</label
              >
              <p class="text-sm text-gray-500 dark:text-gray-400">
                Allow us to track your activity for analytics
              </p>
            </div>
            <Switch
              :model-value="!!preferences.privacy?.tracking"
              @update:model-value="togglePrivacy('tracking')"
            />
          </div>

          <div class="flex items-center justify-between">
            <div>
              <label class="text-sm font-medium text-gray-700 dark:text-gray-200"
                >Data Sharing</label
              >
              <p class="text-sm text-gray-500 dark:text-gray-400">
                Share anonymized usage data to improve our service
              </p>
            </div>
            <Switch
              :model-value="!!preferences.privacy?.dataSharing"
              @update:model-value="togglePrivacy('dataSharing')"
            />
          </div>
        </div>
      </div>

      <!-- Accessibility Settings -->
      <div class="border-t border-gray-200 dark:border-gray-600 pt-8">
        <h3 class="text-base font-medium text-gray-900 dark:text-white mb-4">Accessibility</h3>
        <div class="space-y-4">
          <div class="flex items-center justify-between">
            <div>
              <label class="text-sm font-medium text-gray-700 dark:text-gray-200"
                >Reduce Motion</label
              >
              <p class="text-sm text-gray-500 dark:text-gray-400">
                Minimize animations and transitions
              </p>
            </div>
            <Switch
              :model-value="!!preferences.accessibility?.reduceMotion"
              @update:model-value="toggleAccessibility('reduceMotion')"
            />
          </div>

          <div class="flex items-center justify-between">
            <div>
              <label class="text-sm font-medium text-gray-700 dark:text-gray-200"
                >High Contrast</label
              >
              <p class="text-sm text-gray-500 dark:text-gray-400">
                Increase contrast for better visibility
              </p>
            </div>
            <Switch
              :model-value="!!preferences.accessibility?.highContrast"
              @update:model-value="toggleAccessibility('highContrast')"
            />
          </div>
        </div>
      </div>
    </div>
  </Card>
</template>

<script setup lang="ts">
  import { computed, type Component } from 'vue';
  import { SunIcon, MoonIcon, ComputerDesktopIcon, CheckIcon } from '@heroicons/vue/24/outline';
  import { Card, Button, Switch } from '@asabdev/asab-ui';
  import { useUserProfilePreferences } from '../composables/useUserProfile';

  const { preferences, updateTheme, updateNotifications, updatePreferences } =
    useUserProfilePreferences();

  type ThemeOption = {
    value: 'light' | 'dark' | 'auto';
    label: string;
    icon: Component;
  };

  // Theme options
  const themeOptions: ThemeOption[] = [
    { value: 'light', label: 'Light', icon: SunIcon },
    { value: 'dark', label: 'Dark', icon: MoonIcon },
    { value: 'auto', label: 'Auto', icon: ComputerDesktopIcon },
  ];

  // Timezone options (commonly used timezones)
  const timezoneOptions = computed(() => [
    { value: 'UTC', label: 'UTC (GMT+0)' },
    { value: 'America/New_York', label: 'Eastern Time (GMT-5)' },
    { value: 'America/Chicago', label: 'Central Time (GMT-6)' },
    { value: 'America/Denver', label: 'Mountain Time (GMT-7)' },
    { value: 'America/Los_Angeles', label: 'Pacific Time (GMT-8)' },
    { value: 'Europe/London', label: 'London (GMT+0)' },
    { value: 'Europe/Paris', label: 'Paris (GMT+1)' },
    { value: 'Europe/Berlin', label: 'Berlin (GMT+1)' },
    { value: 'Asia/Tokyo', label: 'Tokyo (GMT+9)' },
    { value: 'Asia/Shanghai', label: 'Shanghai (GMT+8)' },
    { value: 'Asia/Seoul', label: 'Seoul (GMT+9)' },
    { value: 'Australia/Sydney', label: 'Sydney (GMT+11)' },
    { value: preferences.value.timezone, label: `Current: ${preferences.value.timezone}` },
  ]);

  // Methods
  const toggleNotification = (type: keyof typeof preferences.value.notifications) => {
    updateNotifications({
      [type]: !preferences.value.notifications[type],
    });
  };

  const togglePrivacy = (key: keyof typeof preferences.value.privacy) => {
    updatePreferences({
      privacy: {
        ...preferences.value.privacy,
        [key]: !preferences.value.privacy[key],
      },
    });
  };

  const toggleAccessibility = (key: keyof typeof preferences.value.accessibility) => {
    updatePreferences({
      accessibility: {
        ...preferences.value.accessibility,
        [key]: !preferences.value.accessibility[key],
      },
    });
  };

  const updateLanguage = () => {
    // Language change would trigger app-wide language update
    // For now, we just update the preference
    console.log('Language changed to:', preferences.value.language);
  };

  const updateTimezone = () => {
    // Timezone change would affect date/time display throughout the app
    console.log('Timezone changed to:', preferences.value.timezone);
  };

  const exportData = async () => {
    try {
      // This would trigger a data export process
      // For now, we'll just show a notification
      alert('Data export functionality will be implemented in a future update.');
    } catch (error) {
      console.error('Failed to export data:', error);
      alert('Failed to export data. Please try again.');
    }
  };
</script>
